import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Copy, Check, Twitter, Facebook, Linkedin, Mail, X } from 'lucide-react';

/**
 * Share Results Component
 * Allows users to share their airport busyness prediction
 */
const ShareResults = ({ 
  airport,
  date,
  time,
  crowdLevel,
  arrivalTime,
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate shareable URL with parameters
  const generateShareUrl = () => {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      airport: airport?.code || '',
      date: date || '',
      time: time || ''
    });
    return `${baseUrl}?${params.toString()}`;
  };

  // Generate share text
  const generateShareText = () => {
    const levelEmoji = {
      'Low': '🟢',
      'Medium': '🟡', 
      'High': '🟠',
      'Extreme': '🔴'
    };
    
    return `${levelEmoji[crowdLevel] || '✈️'} ${airport?.name || 'Airport'} is expected to be ${crowdLevel} traffic on ${date} at ${time}. Recommended arrival: ${arrivalTime}. Check your airport at:`;
  };

  const shareUrl = generateShareUrl();
  const shareText = generateShareText();

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareLinks = [
    {
      name: 'Twitter',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      color: 'hover:bg-sky-50 hover:text-sky-600'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      color: 'hover:bg-blue-50 hover:text-blue-600'
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      color: 'hover:bg-blue-50 hover:text-blue-700'
    },
    {
      name: 'Email',
      icon: Mail,
      url: `mailto:?subject=${encodeURIComponent(`Airport Busyness: ${airport?.code || 'Check'} on ${date}`)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
      color: 'hover:bg-slate-100 hover:text-slate-700'
    }
  ];

  // Use native share if available
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Airport Busyness: ${airport?.code || ''} on ${date}`,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setIsOpen(true); // Fallback to custom share dialog
        }
      }
    } else {
      setIsOpen(true);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={handleNativeShare}
        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        aria-label="Share results"
      >
        <Share2 className="w-4 h-4" />
        <span>Share</span>
      </button>

      {/* Share Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800">Share Results</h3>
                  <button 
                    onClick={() => setIsOpen(false)}
                    className="p-1 hover:bg-slate-100 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-slate-400" />
                  </button>
                </div>

                {/* Social Share Buttons */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {shareLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex flex-col items-center gap-1 p-3 rounded-lg transition-colors ${link.color}`}
                      title={`Share on ${link.name}`}
                    >
                      <link.icon className="w-5 h-5" />
                      <span className="text-xs">{link.name}</span>
                    </a>
                  ))}
                </div>

                {/* Copy Link */}
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-xs text-slate-500 mb-2">Or copy link</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={shareUrl}
                      readOnly
                      className="flex-1 text-xs px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-slate-600 truncate"
                    />
                    <button
                      onClick={handleCopyLink}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                        copied 
                          ? 'bg-green-100 text-green-600' 
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShareResults;
